import { AuthenticationResult, AuthorizationTicket } from '@pressreader/authentication-types';
import { getCurrentPositionByClient } from '@pressreader/geo';
import { warn } from '@pressreader/logger';
import { GeoPoint } from '@pressreader/types';
import { cookies, getDocumentRefererServerSideFirst } from '@pressreader/utils';

import { GIFTED_ACCESS_TOKEN } from '../constants';

import { httpPost } from './http';
import { getInterfaceLanguage } from './interfacelanguage';
import { getJson } from './services';

const getBasePath = () => window.basePath || '/';

let service = `${getBasePath()}authentication/v1`;

const setAuthServiceBasePath = (path: string) => {
    service = `${path}authentication/v1`;
};

const AUTHORIZATION_TICKETS_ITEM_NAME = `pr_${getBasePath()}authTickets`;

let savedTickets: AuthorizationTicket[] = [];

function saveAuthorizationTickets(tickets: AuthorizationTicket[]) {
    savedTickets = [...tickets];
    window.sessionStorage.setItem(AUTHORIZATION_TICKETS_ITEM_NAME, JSON.stringify(tickets.filter(t => t.session).map(t => t.value)));
    window.localStorage.setItem(AUTHORIZATION_TICKETS_ITEM_NAME, JSON.stringify(tickets.filter(t => !t.session).map(t => t.value)));
}

function getAuthorizationTickets(): string[] {
    const sessionTickets = JSON.parse(window.sessionStorage.getItem(AUTHORIZATION_TICKETS_ITEM_NAME) ?? '[]') as string[];
    const localTickets = JSON.parse(window.localStorage.getItem(AUTHORIZATION_TICKETS_ITEM_NAME) ?? '[]') as string[];
    if (!sessionTickets.length && !localTickets.length && savedTickets?.length) {
        // Restore saved tickets if cookie bot is cleaned up the session storage
        saveAuthorizationTickets(savedTickets);
        return getAuthorizationTickets();
    }
    return [...sessionTickets, ...localTickets];
}

/**
 * @deprecated The canonical auth initialization flow lives in the `Index.cshtml`
 * template, which preloads `window._initAuthResult` before the client bootstraps.
 *
 * This function remains the non-preloaded fallback path in this repo.
 * If auth initialization behavior changes, keep this fallback aligned with the
 * `Index.cshtml` / `window._initAuthResult` implementation.
 */
function initAuthentication(): Promise<AuthenticationResult> {
    // Temporary hack => do not send tickets if PDAuth cookie is present
    const pdAuth = cookies.get('PDAuth');
    const tickets = pdAuth ? [] : getAuthorizationTickets();
    const giftedAccessToken = cookies.get(GIFTED_ACCESS_TOKEN) || undefined;
    return getJson({
        url: `${service}/initialize`,
        data: {
            tickets,
            language: getInterfaceLanguage(),
            urlReferrer: getDocumentRefererServerSideFirst(),
            url: window.location.href,
            giftedAccessToken,
        },
        maxRetries: 3,
        method: 'POST',
        // Every call in this file returns a bearer token minted by this service, so this service's `Date` is the
        // clock that token's `exp` was stamped against — the one reading that cancels device skew instead of
        // adding to it. See `server.clock`; the flag itself is documented on `FetchOptions`.
        trustServerClock: true,
    });
}

function apiSiteSignIn(token: string, enableAutoLogin: boolean): Promise<AuthenticationResult> {
    return httpPost({
        url: `${service}/signIn`,
        data: { signInToken: token, tickets: getAuthorizationTickets(), language: getInterfaceLanguage(), enableAutoLogin },
        requestConfig: { contentType: 'application/json', disableBearerToken: true, trustServerClock: true },
    });
}

async function apiSiteSignOut(): Promise<AuthenticationResult> {
    return httpPost({
        url: `${service}/signout`,
        data: { tickets: getAuthorizationTickets(), language: getInterfaceLanguage() },
        requestConfig: { contentType: 'application/json', disableBearerToken: true, trustServerClock: true },
    });
}

let lastKnowLocation: GeoPoint | undefined = undefined;

async function getGeolocation() {
    if (document.hidden) {
        // Document is hidden, returning last known location
        return lastKnowLocation;
    }
    try {
        const geoLocation = await getCurrentPositionByClient();
        lastKnowLocation = geoLocation;
        return geoLocation;
    } catch (error) {
        warn('Authentication API refreshBearerToken', 'geoLocation', error);
        return undefined;
    }
}

async function refreshBearerToken(useGeoLocation?: boolean): Promise<AuthenticationResult> {
    const geoLocation = useGeoLocation ? await getGeolocation() : undefined;
    return httpPost({
        url: `${service}/refresh`,
        data: {
            tickets: getAuthorizationTickets(),
            language: getInterfaceLanguage(),
            lat: geoLocation?.lat,
            lng: geoLocation?.lng,
        },
        requestConfig: { contentType: 'application/json', disableBearerToken: true, trustServerClock: true },
    });
}

export { apiSiteSignIn, apiSiteSignOut, initAuthentication, refreshBearerToken, saveAuthorizationTickets, setAuthServiceBasePath };
